import { api, type Message } from './api'

export type CommitmentActionName =
  | 'accept'
  | 'keep'
  | 'deny'
  | 'done'
  | 'block'
  | 'snooze'
  | 'setdue'
  | 'setfu'
  | 'chase'

export type CommitmentPickerMode = 'date' | 'datetime' | 'snooze'

export type CommitmentActionDescriptor = {
  action: CommitmentActionName
  commitmentId: string
  value?: string
  reason?: string
  picker?: CommitmentPickerMode
  chatId?: string
  topicId?: string
  followupAt?: string
}

export type CommitmentActionResult = {
  action?: string
  commitment?: {
    id?: string
    followup_at?: string | null
    [key: string]: unknown
  }
  topic_snooze?: {
    ok?: boolean
    snooze_id?: string
    wake_at?: number
    reason?: string
  }
  [key: string]: unknown
}

export type CommitmentCreatePayload = {
  who_owes: string
  what: string
  who_email?: string | null
  due_date?: string | null
  followup_at?: string | null
  project?: string | null
  notes?: string | null
}

const ACTIONS_REQUIRING_PICKER = new Set<CommitmentActionName>(['snooze', 'setdue', 'setfu'])

export function formatLocalDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function formatLocalDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${formatLocalDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function valueFromPickedDate(action: CommitmentActionName, d: Date): string {
  return action === 'setfu' ? formatLocalDateTime(d) : formatLocalDate(d)
}

export function pickerForCommitmentAction(action: CommitmentActionName): CommitmentPickerMode | undefined {
  if (action === 'snooze') return 'snooze'
  if (action === 'setdue') return 'date'
  if (action === 'setfu') return 'datetime'
  return undefined
}

export async function createCommitmentDirect(payload: CommitmentCreatePayload): Promise<unknown> {
  return api('/api/commitments-dashboard/commitments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function applyCommitmentAction(desc: CommitmentActionDescriptor): Promise<CommitmentActionResult> {
  if (ACTIONS_REQUIRING_PICKER.has(desc.action) && !desc.value) {
    throw new Error(`${desc.action} requires a picked date`)
  }
  if (desc.action === 'accept' && (!desc.chatId || !desc.topicId)) {
    throw new Error('Accept needs a review topic to snooze')
  }
  const body: Record<string, string> = {}
  if (desc.value) body.value = desc.value
  if (desc.reason?.trim()) body.reason = desc.reason.trim()
  if (desc.action === 'accept') {
    body.chat_id = desc.chatId!
    body.topic_id = desc.topicId!
  } else if (desc.action === 'setfu' && desc.chatId && desc.topicId) {
    // setfu from a review topic also "accepts" — snooze that topic to the new
    // follow-up. Board / editor calls omit chat/topic and just set the date.
    body.chat_id = desc.chatId
    body.topic_id = desc.topicId
  } else if (desc.action === 'block' && desc.chatId && desc.topicId) {
    body.chat_id = desc.chatId
    body.topic_id = desc.topicId
  } else if (desc.action === 'chase' && desc.chatId && desc.topicId) {
    body.chat_id = desc.chatId
    body.topic_id = desc.topicId
  }
  return api<CommitmentActionResult>(`/api/commitments-dashboard/commitments/${encodeURIComponent(desc.commitmentId)}/actions/${desc.action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function completeCommitmentGroup(groupSourceTaskId: string): Promise<CommitmentActionResult> {
  const groupId = groupSourceTaskId.trim()
  if (!groupId) throw new Error('Commitment group id is required')
  return api<CommitmentActionResult>(
    `/api/commitments-dashboard/commitment-groups/${encodeURIComponent(groupId)}/actions/done`,
    { method: 'POST' },
  )
}

export async function closeCommitmentReviewTopic(chatId: string, topicId: string): Promise<unknown> {
  return api(`/api/chats/${encodeURIComponent(chatId)}/topics/${encodeURIComponent(topicId)}?close_todoist=0`, {
    method: 'DELETE',
  })
}

export function commitmentActionFromMessageAction(
  action: NonNullable<Message['actions']>[number],
): CommitmentActionDescriptor | null {
  if (action.kind === 'commitment_action') {
    const rawAction = String(action.payload?.action ?? '').trim().toLowerCase()
    const rawId = String(action.payload?.commitment_id ?? action.payload?.id ?? '').trim()
    if (!isCommitmentActionName(rawAction) || !rawId) return null
    const rawValue = action.payload?.value
    const value = typeof rawValue === 'string' && rawValue.trim() ? rawValue.trim() : undefined
    const rawFollowup = action.payload?.followup_at
    const followupAt = typeof rawFollowup === 'string' && rawFollowup.trim()
      ? rawFollowup.trim()
      : undefined
    return {
      action: rawAction,
      commitmentId: rawId,
      value,
      followupAt,
      picker: value ? undefined : pickerForCommitmentAction(rawAction),
    }
  }
  if (action.kind !== 'reply_as_mark') return null
  return parseCommitmentCommand(String(action.payload?.text ?? '').trim())
}

export function parseCommitmentCommand(text: string): CommitmentActionDescriptor | null {
  const simple = /^(ACCEPT|KEEP|DENY|DONE|CHASE)\s+(\S+)\s*$/i.exec(text)
  if (simple) {
    return {
      action: simple[1].toLowerCase() as CommitmentActionName,
      commitmentId: simple[2],
    }
  }

  const snooze = /^SNOOZE\s+(\S+)(?:\s+\S+)?\s*$/i.exec(text)
  if (snooze) {
    return { action: 'snooze', commitmentId: snooze[1], picker: 'snooze' }
  }

  const setDue = /^SETDUE\s+(\S+)(?:\s+(\d{4}-\d{2}-\d{2}))?\s*$/i.exec(text)
  if (setDue) {
    return {
      action: 'setdue',
      commitmentId: setDue[1],
      value: setDue[2],
      picker: setDue[2] ? undefined : 'date',
    }
  }

  const setFollowUp = /^SETFU\s+(\S+)(?:\s+(.+?))?\s*$/i.exec(text)
  if (setFollowUp) {
    const value = setFollowUp[2]?.trim()
    return {
      action: 'setfu',
      commitmentId: setFollowUp[1],
      value: value && value !== 'YYYY-MM-DD HH:MM' ? value : undefined,
      picker: value && value !== 'YYYY-MM-DD HH:MM' ? undefined : 'datetime',
    }
  }

  return null
}

function isCommitmentActionName(value: string): value is CommitmentActionName {
  return value === 'keep'
    || value === 'accept'
    || value === 'deny'
    || value === 'done'
    || value === 'block'
    || value === 'snooze'
    || value === 'setdue'
    || value === 'setfu'
    || value === 'chase'
}
